// src/component/BannerSlider.jsx
import React, { useEffect, useMemo, useState } from "react";

export default function BannerSlider({
  items = [],
  aspectClassName = "aspect-[21/9]",
  className = "",
  autoMs = 3500,
  showDots = true,
  onClickItem,
  priority = false, // LCP 후보(홈 메인 배너)면 lazy 를 끄고 우선 로드
}) {
  // App(banner_component.dart)과 동일하게 index 오름차순으로 노출한다.
  // index가 없는 아이템(서브 배너 등)은 stable sort 특성상 원래 순서를 유지한다.
  const safeItems = useMemo(
    () =>
      (Array.isArray(items) ? items.filter(Boolean) : [])
        .slice()
        .sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0)),
    [items]
  );
  const [idx, setIdx] = useState(0);
  // 수동 이동(화살표/도트) 직후 자동 슬라이드 타이머를 재시작해
  // 클릭하자마자 다음 배너로 넘어가는 현상을 막는다.
  const [navTick, setNavTick] = useState(0);

  const goTo = (updater) => {
    setIdx(updater);
    setNavTick((t) => t + 1);
  };

  useEffect(() => {
    setIdx(0);
  }, [safeItems.length]);

  useEffect(() => {
    if (safeItems.length <= 1) return;
    const t = setInterval(() => {
      setIdx((p) => (p + 1) % safeItems.length);
    }, autoMs);
    return () => clearInterval(t);
  }, [safeItems.length, autoMs, navTick]);

  if (safeItems.length === 0) return null;

  const current = safeItems[idx];

  return (
    <div className={className}>
      <div className="relative overflow-hidden rounded-2xl border border-border-tertiary bg-white">
        <button
          type="button"
          onClick={() => onClickItem?.(current)}
          className="w-full text-start block"
        >
          <div className={`${aspectClassName} w-full bg-bg-secondary`}>
            {current?.image ? (
              <img
                src={current.image}
                alt={current?.title ?? "banner"}
                className="h-full w-full object-cover block"
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : "auto"}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-text-tertiary text-body-md">
                Banner
              </div>
            )}
          </div>
        </button>

        {safeItems.length > 1 ? (
          <>
            <button
              type="button"
              onClick={() => goTo((p) => (p - 1 + safeItems.length) % safeItems.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-black/30 text-white transition-colors hover:bg-black/50"
              aria-label="previous banner"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => goTo((p) => (p + 1) % safeItems.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-black/30 text-white transition-colors hover:bg-black/50"
              aria-label="next banner"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </>
        ) : null}

        {showDots && safeItems.length > 1 ? (
          <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2">
            {safeItems.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(() => i)}
                className={`h-2 w-2 rounded-full ${i === idx ? "bg-white" : "bg-white/50"} block`}
                aria-label={`banner-${i + 1}`}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
